/*
  MOVIQ (snelheid): filteren en sorteren op de collectiepagina sneller maken.

  1) Direct ophalen
     Horizon (facets.js) start het ophalen van de gefilterde productlijst pas binnen
     startViewTransition. facets.js roept bij een filterklik eerst history.pushState aan;
     dit script haakt daarop in en start het ophalen meteen via sectionRenderer.getSectionHTML
     (zonder signal). sectionRenderer hergebruikt die lopende aanvraag daarna automatisch,
     dus er gaat maar 1 verzoek naar de server.

  2) Geen overgangsanimatie bij filteren
     Voor de animatie meet Horizon eerst alle productkaarten op (requestIdleCallback +
     getCardsToAnimate, ~400 ms) en maakt de browser een snapshot van de hele pagina.
     Direct na een filter-pushState verbergen we document.startViewTransition heel even
     (tot de volgende taak), zodat Horizon de update meteen en zonder animatie uitvoert.
     De animatie bij het wisselen van rasterweergave blijft gewoon werken.

  3) Kruisjes bij actieve filters rekenen vanaf de huidige filters
     Het thema gebruikte de vaste link (data-url) uit de vorige paginalading. Klikte je snel
     twee kruisjes, dan zette het tweede kruisje het net verwijderde filter terug.
     Nu wordt bij een klik alleen die ene waarde uit de huidige URL gehaald
     (data-param-name / data-value of data-price-params, zie snippets/filter-remove-buttons.liquid).
     Het kruisje verdwijnt meteen en het bijbehorende vinkje gaat direct uit.
     Bij 'Alles wissen' (data-clear-all) gaan alle vinkjes en kruisjes direct uit.

  Geladen vanuit sections/main-collection.liquid.
*/
import { sectionRenderer } from '@theme/section-renderer';

if (!window.__moviqFacetsPrefetch) {
  window.__moviqFacetsPrefetch = true;

  const suppressViewTransitionOnce = () => {
    if (typeof document.startViewTransition !== 'function') return;
    if (Object.prototype.hasOwnProperty.call(document, 'startViewTransition')) return;
    document.startViewTransition = undefined;
    setTimeout(() => {
      delete document.startViewTransition;
    }, 0);
  };

  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    const before = window.location.href;
    const result = originalPushState.apply(this, args);

    try {
      const after = window.location.href;
      if (after !== before && new URL(before).pathname === window.location.pathname) {
        const form = document.querySelector('facets-form-component[section-id]');
        const sectionId = form && form.getAttribute('section-id');
        const isPagination = new URL(after).searchParams.has('page');
        if (sectionId && !isPagination) {
          suppressViewTransitionOnce();
          const useCache = !(window.Shopify && window.Shopify.designMode);
          sectionRenderer.getSectionHTML(sectionId, useCache).catch(() => {});
        }
      }
    } catch (error) {
      /* nooit de filterwerking blokkeren */
    }

    return result;
  };

  // 3) Kruisjes bij actieve filters en 'Alles wissen'
  customElements.whenDefined('facet-remove-component').then(() => {
    const FacetRemove = customElements.get('facet-remove-component');
    const originalRemoveFilter = FacetRemove && FacetRemove.prototype.removeFilter;
    if (typeof originalRemoveFilter !== 'function') return;

    FacetRemove.prototype.removeFilter = function (args, event) {
      try {
        const isKey = event instanceof KeyboardEvent;
        const activates = !isKey || event.key === 'Enter' || event.key === ' ';
        const name = this.dataset.paramName;
        const value = this.dataset.value;
        const priceParams = this.dataset.priceParams;

        if (activates && this.hasAttribute('data-clear-all')) {
          // Alles wissen: direct alle vinkjes uit en alle kruisjes weg
          document.querySelectorAll('facets-form-component input[type="checkbox"][name^="filter."]').forEach((input) => {
            input.checked = false;
          });
          document.querySelectorAll('facet-remove-component.facets-remove__pill').forEach((pill) => {
            pill.hidden = true;
          });
        } else if (activates && (name || priceParams)) {
          const url = new URL(window.location.href);
          url.searchParams.delete('page');

          if (priceParams) {
            priceParams.split(',').forEach((param) => param && url.searchParams.delete(param));
          } else {
            const kept = url.searchParams.getAll(name).filter((v) => v !== value);
            url.searchParams.delete(name);
            kept.forEach((v) => url.searchParams.append(name, v));
          }

          this.dataset.url = url.pathname + url.search;

          // Direct zichtbaar maken: vinkje uit en kruisje weg
          if (name) {
            document.querySelectorAll('facets-form-component input[type="checkbox"]').forEach((input) => {
              if (input.name === name && input.value === value) input.checked = false;
            });
          }
          this.hidden = true;
        }
      } catch (error) {
        /* val terug op het standaardgedrag van het thema */
      }

      return originalRemoveFilter.call(this, args, event);
    };
  });
}

/*
  Gedeelde helper voor de MOVIQ-snelfilters (sections/moviq-type-filter.liquid en
  sections/moviq-mobile-filterbar.liquid): productlijst verversen zonder de hele pagina
  te herladen. Valt terug op een gewone paginalading als het thema-onderdeel ontbreekt.
*/
window.moviqSoftNavigate = function (url) {
  const href = url.toString();
  try {
    const forms = [...document.querySelectorAll('facets-form-component')];
    const facets = forms.find((el) => !el.closest('dialog')) || forms[0];
    if (facets && typeof facets.updateFiltersByURL === 'function') {
      facets.updateFiltersByURL(href);
      return;
    }
  } catch (error) {
    /* val terug op gewone navigatie */
  }
  window.location.assign(href);
};
